// src/achievements/index.js — 🏆 Achievements UI + the bespoke badge artwork
// (v126.2, PR3). Loaded by BOTH apps as dist/achievements.js (globalName
// Achievements). Display-only: this bundle never writes anything.
//
// Artwork: a hand-drawn SVG rosette — scalloped ring, ribbon tails, tier ring
// (week green / month silver / quarter gold / year royal / ⭐ amber), badge
// glyph centre. One drawing, themed per badge + period; crisp at any size,
// ~1.5KB, no image files, no Storage.

import { badgeArtSvg, medalFor } from './art.js';   // v129.49 — the designed badge set
import { visiblePoll } from '../shared/visible-poll.js';   // v136.8: no shelf refresh while the page is hidden
function api(path) {
  var base = (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
  return fetch(base + path, { cache: 'no-store' });
}
function esc(s) {
  return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// ── The artwork ──────────────────────────────────────────────────────────────
var TIER_COLOURS = {
  week:    { ring: '#009E3D', rib: '#00742d', soft: '#e6f6ec' },   // ELC green
  month:   { ring: '#8a94a6', rib: '#5f6b80', soft: '#eef1f5' },   // silver
  quarter: { ring: '#d4a017', rib: '#a87d0e', soft: '#faf3df' },   // gold
  year:    { ring: '#7c3aed', rib: '#5b21b6', soft: '#f1eafd' },   // royal
  star:    { ring: '#f59e0b', rib: '#b45309', soft: '#fef3e2' },   // ⭐ amber
  ongoing: { ring: '#0e7490', rib: '#155e75', soft: '#e0f2f7' },   // teal
};
function tierOf(period, badgeKey) {
  if (badgeKey === 'star') return 'star';
  var p = String(period || '');
  if (/^\d{4}-W\d{2}$/.test(p)) return 'week';
  if (/^\d{4}-\d{2}$/.test(p)) return 'month';
  if (/^\d{4}-Q[1-4]$/.test(p)) return 'quarter';
  if (/^\d{4}$/.test(p)) return 'year';
  return 'ongoing';
}
function periodLabel(period) {
  var p = String(period || '');
  var m;
  if ((m = /^(\d{4})-W(\d{2})$/.exec(p))) return 'Wk ' + Number(m[2]);
  if ((m = /^(\d{4})-(\d{2})$/.exec(p))) return ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][Number(m[2]) - 1] + ' ' + m[1].slice(2);
  if ((m = /^(\d{4})-Q([1-4])$/.exec(p))) return 'Q' + m[2] + ' ' + m[1].slice(2);
  if (/^\d{4}$/.test(p)) return p;
  if ((m = /^m(\d+):\d+$/.exec(p))) return 'Module ' + Number(m[1]);   // v133.102: per-module badges (m1:1 ...)
  return '';
}
// The rosette. size in px; glyph = the badge emoji; tier keys TIER_COLOURS.
function badgeSvg(glyph, tier, size, label) {
  var c = TIER_COLOURS[tier] || TIER_COLOURS.ongoing;
  size = size || 64;
  // 12 scallop bumps on a 100-unit canvas
  var bumps = '';
  for (var i = 0; i < 12; i++) {
    var a = (i / 12) * Math.PI * 2;
    bumps += '<circle cx="' + (50 + 38 * Math.cos(a)).toFixed(1) + '" cy="' + (44 + 38 * Math.sin(a)).toFixed(1) + '" r="7.5" fill="' + c.ring + '"/>';
  }
  return '<svg width="' + size + '" height="' + size + '" viewBox="0 0 100 110" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="badge">' +
    '<polygon points="34,78 42,104 50,92 58,104 66,78" fill="' + c.rib + '"/>' +          // ribbon tails
    bumps +
    '<circle cx="50" cy="44" r="38" fill="' + c.ring + '"/>' +
    '<circle cx="50" cy="44" r="31" fill="' + c.soft + '"/>' +
    '<circle cx="50" cy="44" r="31" fill="none" stroke="#ffffff" stroke-width="2" opacity="0.7"/>' +
    '<text x="50" y="53" font-size="30" text-anchor="middle">' + glyph + '</text>' +
    (label ? '<text x="50" y="86" font-size="11" font-weight="700" text-anchor="middle" fill="#ffffff">' + esc(label) + '</text>' : '') +
    '<ellipse cx="38" cy="28" rx="12" ry="6" fill="#ffffff" opacity="0.25" transform="rotate(-25 38 28)"/>' + // shine
    '</svg>';
}
// v129.49 — the DESIGNED set first (Matt approved the gallery, 4 Aug); the
// scallop/emoji badge stays as the fallback for any key without art. A new
// badge must degrade, never blank.
function awardSvg(a, size) {
  try {
    var art = badgeArtSvg(a.badge_key, size, medalFor(a));
    if (art) return art;
  } catch (e) { /* art is garnish — fall through to the emoji badge */ }
  var tier = tierOf(a.period, a.badge_key);
  return badgeSvg(a.emoji || '🏅', tier, size, periodLabel(a.period));
}

// ── Data ─────────────────────────────────────────────────────────────────────
var _storeCache = null;      // { store, store_awards } for the signed-in store
function myStore() {
  try { return (typeof currentStore !== 'undefined' && currentStore) ? (currentStore.name || currentStore) : null; } catch (e) { return null; }
}
async function fetchStoreAwards() {
  var st = myStore();
  if (!st) return null;
  try {
    var res = await api('/api/achievements?store=' + encodeURIComponent(st));
    if (!res.ok) return null;
    var d = await res.json();
    if (d && d.ok) { _storeCache = d; return d; }
  } catch (e) { /* offline — shelf just stays empty */ }
  return null;
}
function isFreshAward(a) {
  var t = Date.parse(a.awarded_at);
  return !isNaN(t) && (Date.now() - t) < 7 * 24 * 3600 * 1000;
}
function starActive(a) {
  return a.badge_key === 'star' && a.expires_at && Date.parse(a.expires_at) > Date.now();
}

// v126.20 (Matt: "if they have one it should show next to their clock in
// name") — a SYNCHRONOUS lookup for the clock roster: reads the 15-minute
// store cache (staff_stars rides the same GET). Empty until the first fetch
// lands — the next roster repaint picks it up. The reason shows on hover.
// v129.27 (Matt: "awarded stars should only show for 7 days on their clock in
// tab, but they last forever on their record. Same with blue hearts") — the
// clock screen is a LIVE view of today's floor, so a mark earned three months
// ago sitting beside a name says nothing about now. Seven days keeps it a
// current, visible congratulation instead of permanent decoration.
//
// This is a DISPLAY window, deliberately not an expiry: nothing is written,
// nothing is deleted. The awards wall (renderAwardsScreen, "NEVER expires
// off") and My Development (renderPersonalBadges) still show everything, for
// keeps. Only this one strip beside the name is time-boxed.
var RECENT_MARK_DAYS = 7;

function withinRecentWindow(ts) {
  if (!ts) return false;
  var t = Date.parse(ts);
  if (isNaN(t)) return false;
  var age = Date.now() - t;
  return age >= 0 && age < RECENT_MARK_DAYS * 86400000;
}

function starFor(staffId) {
  var out = '';
  // ⭐ a hand-granted star — still honours an explicit expires_at if one is set
  var stars = (_storeCache && _storeCache.staff_stars) || [];
  for (var i = 0; i < stars.length; i++) {
    var x = stars[i];
    if (String(x.staff_id) !== String(staffId)) continue;
    if (x.expires_at && Date.parse(x.expires_at) <= Date.now()) continue;
    if (!withinRecentWindow(x.awarded_at || x.created_at)) continue;
    out += ' <span title="' + esc(x.reason || 'Star badge') + '" style="font-size:14px">⭐</span>';
    break;
  }
  // 💙 a Customer Service Heart earned this week, same window
  var roster = (_storeCache && _storeCache.roster_awards) || [];
  for (var j = 0; j < roster.length; j++) {
    var a = roster[j];
    if (String(a.staff_id) !== String(staffId)) continue;
    if (a.badge_key !== 'customer-heart') continue;
    if (!withinRecentWindow(a.awarded_at)) continue;
    out += ' <span title="' + esc(a.reason || 'Customer Service Heart') + '" style="font-size:14px">💙</span>';
    break;
  }
  return out;
}

// ── The store shelf (clock screen) ───────────────────────────────────────────
async function renderShelf() {
  // v126.9 (Matt: "in KPIs there should be badges as well") — the same shelf
  // fills BOTH containers: the clock screen and the KPI screen.
  var els = ['storeBadgeShelf', 'storeBadgeShelfKpi']
    .map(function (id) { return document.getElementById(id); })
    .filter(Boolean);
  if (!els.length) return;
  var d = _storeCache || await fetchStoreAwards();

  // v126.23 (Matt: "I CAN SEE [A STAFF MEMBER] HAS A STAR — STILL NOT SHOWING IN THE
  // KPI/BADGES") — the Badges tab is the STORE shelf, but the team's PERSONAL
  // ⭐ stars belong here too. Active stars ride the same fetch (with names).
  function teamStarsHtml() {
    var stars = (d && d.staff_stars) || [];
    var live = stars.filter(function (x) { return !x.expires_at || Date.parse(x.expires_at) > Date.now(); });
    if (!live.length) return '';
    return '<div style="padding:6px 14px 10px">' +
      '<div style="font-size:11px;font-weight:800;color:var(--sub,#5e6863);text-transform:uppercase;letter-spacing:.04em;padding:2px 0 6px">⭐ Team stars this week</div>' +
      live.slice(0, 8).map(function (s) {
        // v131.79 (Matt: "Even when someone gets an award can it show their face?").
        // staff_stars already carries staff_id - the name was looked up FROM it -
        // so the face costs nothing but a chip the sweep then hydrates. align-items
        // moves baseline -> center so the circle sits with the text, not under it.
        var face = (s.staff_id != null && typeof Avatars !== 'undefined' && Avatars.chip)
          ? Avatars.chip('staff', s.staff_id, s.name, 20) : '';
        return '<div style="display:flex;gap:8px;align-items:center;padding:2px 0;font-size:13px"><span>⭐</span>' + face +
          '<span translate="no" style="font-weight:800">' + esc(s.name || 'Team member') + '</span>' +
          (s.reason ? '<span translate="no" style="font-size:12px;color:var(--ui-warn)">&ldquo;' + esc(s.reason) + '&rdquo;</span>' : '') + '</div>';
      }).join('') + '</div>';
  }

  if (!d || !d.store_awards || !d.store_awards.length) {
    // v126.15 (Matt: "i still cant see the badges or how they work?") — empty
    // is INVISIBLE on the clock screen (no clutter) but the KPI Badges tab
    // says so honestly instead of showing a blank pane (team ⭐ stars still
    // show above the note when someone has one).
    els.forEach(function (el) {
      if (el.id === 'storeBadgeShelfKpi') {
        el.style.display = 'block';
        el.innerHTML = teamStarsHtml() + '<div style="padding:16px 14px;text-align:center">' +
          '<div style="font-size:30px">🏆</div>' +
          '<div style="font-weight:800;font-size:14px;margin-top:4px">No store badges yet</div>' +
          '<div style="font-size:12px;color:var(--sub,#5e6863);margin-top:4px;line-height:1.5">Badges are computed automatically overnight from real results — the first ones appear after the next sweep (Head Office can also backfill history in one tap).</div></div>';
      } else {
        el.innerHTML = ''; el.style.display = 'none';
      }
    });
    return;
  }
  var awards = d.store_awards.slice(0, 8);
  var html =
    '<div style="display:flex;align-items:center;gap:6px;padding:8px 14px 2px">' +
      '<span style="font-size:11px;font-weight:800;color:var(--sub,#5e6863);text-transform:uppercase;letter-spacing:.04em">🏆 Store achievements</span>' +
      '<button onclick="Achievements.open()" style="margin-inline-start:auto;border:none;background:none;color:var(--elc-green,#009E3D);font-weight:800;font-size:12px;cursor:pointer;font-family:inherit">See all ›</button></div>' +
    '<div style="display:flex;gap:8px;overflow-x:auto;padding:4px 14px 10px;-webkit-overflow-scrolling:touch">' +
    awards.map(function (a) {
      return '<div title="' + esc(a.name) + ' — ' + esc(a.period) + '" style="flex-shrink:0;text-align:center;width:64px' + (isFreshAward(a) ? ';filter:drop-shadow(0 0 6px rgba(212,160,23,.8))' : '') + '">' +
        awardSvg(a, 56) +
        '<div style="font-size:9px;font-weight:700;color:var(--sub,#5e6863);line-height:1.15;margin-top:2px">' + esc(a.name) + '</div></div>';
    }).join('') + '</div>';
  els.forEach(function (el) {
    el.style.display = 'block';
    // The KPI pane also carries the team's ⭐ stars (v126.23).
    el.innerHTML = html + (el.id === 'storeBadgeShelfKpi' ? teamStarsHtml() : '');
  });
}

// ── The trophy cabinet (full-screen overlay — an overlay tab, allowed outside
// #app like openMyDevelopment; the v124.7 rule only binds switchScreen tabs) ──
function open() {
  var old = document.getElementById('achOverlay');
  if (old) old.remove();
  var wrap = document.createElement('div');
  wrap.id = 'achOverlay';
  wrap.style.cssText = 'position:fixed;inset:0;background:var(--bg);z-index:9000;display:flex;flex-direction:column;overflow:hidden';
  wrap.innerHTML =
    '<div style="display:flex;align-items:center;gap:10px;padding:14px 16px;background:var(--elc-green,#009E3D);color:#fff">' +
      '<div style="font-size:20px">🏆</div>' +
      '<div style="flex:1"><div style="font-weight:900;font-size:16px">Achievements</div>' +
      '<div id="achOvStore" style="font-size:12px;opacity:.9"></div></div>' +
      '<button onclick="Achievements.close()" style="border:none;background:rgba(255,255,255,.18);color:#fff;font-size:18px;width:34px;height:34px;border-radius:17px;cursor:pointer">×</button></div>' +
    '<div id="achOvBody" style="flex:1;overflow-y:auto;padding:14px">Loading…</div>';
  document.body.appendChild(wrap);
  renderCabinet();
}
function close() {
  var el = document.getElementById('achOverlay');
  if (el) el.remove();
}
async function renderCabinet() {
  var body = document.getElementById('achOvBody');
  if (!body) return;
  var d = await fetchStoreAwards();
  var head = document.getElementById('achOvStore');
  if (head) head.textContent = (d && d.store) || myStore() || '';
  if (!d || !d.store_awards || !d.store_awards.length) {
    body.innerHTML = '<div style="text-align:center;padding:40px 20px;color:var(--tiny)">' +
      '<div style="font-size:40px;margin-bottom:8px">🏆</div>' +
      '<div style="font-weight:800;font-size:15px;color:var(--text)">No badges yet — they\'re coming</div>' +
      '<div style="font-size:13px;margin-top:6px;line-height:1.5">Badges are awarded automatically for demos, add-ons, reviews, SVRs, training and compliance — weekly, monthly, quarterly and yearly. Keep being brilliant and this cabinet fills itself.</div></div>';
    return;
  }
  // ⭐ live stars first (with the written reason), then everything by recency.
  var stars = d.store_awards.filter(starActive);
  var rest = d.store_awards.filter(function (a) { return !starActive(a); });
  var html = '';
  if (stars.length) {
    html += '<div style="font-weight:900;font-size:13px;margin:2px 0 8px;color:var(--ui-warn)">⭐ Star badges — this week</div>';
    stars.forEach(function (a) {
      html += '<div style="display:flex;gap:12px;align-items:center;background:var(--card);border:1px solid var(--stores-warn-line);border-radius:14px;padding:10px 12px;margin-bottom:8px">' +
        awardSvg(a, 54) +
        '<div style="flex:1;min-width:0"><div style="font-weight:800;font-size:14px">Star Badge</div>' +
        '<div translate="no" style="font-size:12.5px;color:var(--tiny);line-height:1.4">' + esc(a.reason || '') + '</div></div></div>';
    });
  }
  html += '<div style="font-weight:900;font-size:13px;margin:10px 0 8px;color:var(--text)">Trophy cabinet</div>' +
    '<div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(96px,1fr));gap:10px">' +
    rest.map(function (a) {
      return '<div style="background:var(--card);border:1px solid var(--border);border-radius:14px;padding:10px 6px;text-align:center' + (isFreshAward(a) ? ';box-shadow:0 0 0 2px #d4a01766' : '') + '">' +
        awardSvg(a, 64) +
        '<div style="font-size:11px;font-weight:800;line-height:1.2;margin-top:4px">' + esc(a.name) + '</div>' +
        '<div style="font-size:10px;color:var(--stores-ink-3);margin-top:2px">' + esc(periodLabel(a.period) || 'earned') + (a.value != null ? ' · ' + esc(a.value) : '') + '</div></div>';
    }).join('') + '</div>';
  body.innerHTML = html;
}

// ── The clock-in moment (called by the clock flow, fully guarded there) ──────
// Their newest personal badges float up for a few seconds — the dopamine hit.
async function onClockIn(staffId, name) {
  try {
    var res = await api('/api/achievements?staff_id=' + encodeURIComponent(staffId));
    if (!res.ok) return;
    var d = await res.json();
    var mine = (d && d.staff_awards) || [];
    var show = mine.filter(starActive).concat(mine.filter(function (a) { return !starActive(a); })).slice(0, 3);
    if (!show.length) return;
    var old = document.getElementById('achClockCard');
    if (old) old.remove();
    var card = document.createElement('div');
    card.id = 'achClockCard';
    card.style.cssText = 'position:fixed;left:50%;bottom:84px;transform:translateX(-50%);z-index:9500;background:var(--card);border:1px solid var(--border);border-radius:16px;box-shadow:0 12px 32px rgba(0,0,0,.18);padding:12px 16px;display:flex;gap:10px;align-items:center;max-width:92vw';
    card.innerHTML = '<div style="display:flex;gap:6px">' +
      show.map(function (a) { return awardSvg(a, 44); }).join('') + '</div>' +
      '<div style="min-width:0"><div style="font-weight:900;font-size:13px">' + esc(String(name || '').split(' ')[0]) + ' — your badges 🎉</div>' +
      '<div style="font-size:11.5px;color:var(--tiny);line-height:1.3">' + show.map(function (a) { return esc(a.name); }).join(' · ') + '</div></div>';
    card.addEventListener('click', function () { card.remove(); });
    document.body.appendChild(card);
    setTimeout(function () { try { card.remove(); } catch (e) {} }, 6000);
  } catch (e) { /* the clock flow must never notice us */ }
}

// ── My Development badge case (rendered when the container exists) ───────────
async function renderPersonalBadges(staffId, containerId) {
  var el = document.getElementById(containerId || 'mydevBadges');
  if (!el || !staffId) return;
  try {
    var res = await api('/api/achievements?staff_id=' + encodeURIComponent(staffId));
    if (!res.ok) return;
    var d = await res.json();
    var mine = (d && d.staff_awards) || [];
    // v126.20 (Matt: "how do they update their profile pic?") — My Development
    // is the natural personal home, so the photo button lives here PERMANENTLY
    // (with or without badges), alongside the chat name-picker entry point.
    var photoBtn = '<button onclick="if(typeof Avatars!==\'undefined\'&&Avatars.openCapture)Avatars.openCapture()" style="margin:10px 0 4px;border:1.5px solid var(--elc-green,#009E3D);background:var(--card);color:var(--elc-green,#009E3D);font-weight:800;font-size:12.5px;padding:8px 14px;border-radius:10px;cursor:pointer;font-family:inherit">📷 Add / change your profile photo</button>';
    if (!mine.length) { el.innerHTML = photoBtn; return; }
    el.innerHTML = '<div style="font-weight:800;font-size:13px;margin:12px 0 6px">🏅 My badges</div>' +
      '<div style="display:flex;gap:8px;overflow-x:auto;padding-bottom:6px">' +
      mine.slice(0, 12).map(function (a) {
        // v133.102: WHICH month or module, under the name. Repeats are real (Right On Time is monthly, Module
        // Passed is per module) but looked like duplicates: the designed art carries no period (Wafi City,
        // 7 Oct 2026).
        var when = periodLabel(a.period);
        return '<div title="' + esc(a.name + (when ? ' (' + when + ')' : '')) + '" style="flex-shrink:0;text-align:center;width:60px">' + awardSvg(a, 52) +
          '<div style="font-size:9px;font-weight:700;color:var(--tiny);line-height:1.15;margin-top:2px">' + esc(a.name) + '</div>' +
          (when ? '<div style="font-size:8.5px;color:var(--stores-ink-3);line-height:1.15">' + esc(when) + '</div>' : '') + '</div>';
      }).join('') + '</div>' + photoBtn;
  } catch (e) { /* never break My Development */ }
}

// ── Boot: keep the shelf fresh without touching the boot payload ─────────────
var _booted = false;
function boot() {
  if (_booted || typeof document === 'undefined') return;
  _booted = true;
  var tries = 0;
  var t = setInterval(function () {
    tries++;
    // v136.8: the 15-minute refresh rests while the page is hidden (one catch-up on return)
    if (myStore()) { clearInterval(t); renderShelf(); visiblePoll(function () { _storeCache = null; renderShelf(); }, 15 * 60 * 1000); }
    else if (tries > 60) clearInterval(t);          // no store sign-in on this device
  }, 2000);
}
if (typeof window !== 'undefined') boot();

// ── 🏆 the Awards TAB (v129.0, Matt: "should it be its own tab?" + "it doesnt
// keep or show individually awarded ones") — a real switchScreen tab: the
// store badge case in full, then EVERY rostered person's award history
// (all badge keys, no expiry filter — an earned badge stays on the wall).
async function renderAwardsScreen() {
  var root = document.getElementById('awardsRoot');
  if (!root) return;
  root.innerHTML = '<div style="padding:24px;text-align:center;color:var(--sub);font-size:13px">Loading awards…</div>';
  var d = await fetchStoreAwards();
  if (!d) { root.innerHTML = '<div style="padding:24px;text-align:center;color:var(--sub);font-size:13px">Sign in to see awards.</div>'; return; }
  var html = '';
  var store = (d.store_awards || []);
  html += '<div class="td-card"><div class="td-card-title">🏆 Store achievements</div>';
  if (store.length) {
    html += '<div style="display:flex;flex-wrap:wrap;gap:10px;padding:8px 10px 12px">' +
      store.slice(0, 24).map(function (a) {
        return '<div title="' + esc(a.name) + ' — ' + esc(a.period) + '" style="text-align:center;width:70px">' +
          awardSvg(a, 56) +
          '<div style="font-size:9px;font-weight:700;color:var(--sub,#5e6863);line-height:1.15;margin-top:2px">' + esc(a.name) + '</div>' +
          '<div style="font-size:9px;color:var(--tiny)">' + esc(periodLabel(a.period)) + '</div></div>';
      }).join('') + '</div>';
  } else {
    html += '<div style="padding:10px 12px 14px;font-size:12.5px;color:var(--sub)">No store badges yet — they are computed overnight from real results.</div>';
  }
  html += '</div>';
  // the individual wall — grouped by person, newest first, NEVER expires off
  var roster = (d.roster_awards || []);
  html += '<div class="td-card"><div class="td-card-title">🎉 Individual awards</div>';
  if (roster.length) {
    var byName = {};
    var order = [];
    roster.forEach(function (a) {
      if (!byName[a.staff_name]) { byName[a.staff_name] = []; order.push(a.staff_name); }
      byName[a.staff_name].push(a);
    });
    html += order.map(function (n) {
      var rows = byName[n].slice(0, 10).map(function (a) {
        return '<div style="display:flex;gap:8px;align-items:baseline;padding:2px 0;font-size:13px">' +
          '<span>' + (a.emoji || '\ud83c\udfc5') + '</span><span>' + esc(a.name || a.badge_key || 'Badge') + '</span>' +
          (a.reason ? '<span translate="no" style="font-size:12px;color:var(--ui-warn)">\u201c' + esc(a.reason) + '\u201d</span>' : '') +
          '<span style="margin-inline-start:auto;font-size:11px;color:var(--tiny)">' + esc(String(a.awarded_at || '').slice(0, 10)) + '</span></div>';
      }).join('');
      return '<div style="padding:6px 12px 10px"><div translate="no" style="font-weight:800;font-size:13.5px" data-av-kind="none">' + esc(n) + '</div>' + rows + '</div>';
    }).join('');
  } else {
    html += '<div style="padding:10px 12px 14px;font-size:12.5px;color:var(--sub)">No individual awards yet — Head Office can grant a \u2b50 star any time, and earned badges land here for keeps.</div>';
  }
  html += '</div>';
  root.innerHTML = html;
}
window.renderAwardsScreen = renderAwardsScreen;

export { badgeSvg, awardSvg, tierOf, periodLabel, renderShelf, open, close, onClockIn, renderPersonalBadges, starFor, renderAwardsScreen };
